import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  alpha,
  CircularProgress,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "../../context";
import { store } from "../../redux/store";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import {
  AddQuizContentByIdCall,
  DeleteQuizContentByIdCall,
  EditQuizContentByIdCall,
  GetAllQuizByIdCall,
  GetQuizTitlesCall,
} from "../../redux/api/quizTitle.api";
import { GetTitlesCall } from "../../redux/api/title.api";
import { generateQuestionCall } from "../../redux/api/ai.api";
import "./map.css";
import CustomTranslationDrawer from "./CustomTranslationDrawer";

const INPUT_HEIGHT_SX = { "& .MuiInputBase-root": { minHeight: 44 } };
const MENU_PROPS = { PaperProps: { sx: { maxHeight: 320, bgcolor: "background.paper", color: "text.primary" } } };
const QUESTION_TRUNCATE_LENGTH = 120;
const noImageUrl = require("../../assets/images/no-image.jpg");

function stripHtml(html) {
  if (typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function getTitleLabel(item, field = "title") {
  const list = item?.titleLocalizations?.filter((e) => e.field === field) || [];
  return list[0]?.value || "";
}
function getSlideImageUrl(subtitle) {
  if (subtitle?.image) return subtitle.image;
  const slide = subtitle?.slides?.[0];
  const file = slide?.Files?.[0] || slide?.files?.[0];
  const url = file?.url;
  if (url) return url + "/3/3/3.png";
  return null;
}
function getSlideTileUrl(entry) {
  if (!entry?.imageUrl) return "";
  return entry.imageUrl.replace(/\/3\/3\/3\.png$/i, "") + "/{z}/{x}/{y}.png";
}
function buildSlideEntries(titles, sourceLabel) {
  const entries = [];
  const is3D = sourceLabel === "3D Model";
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title");
    (tile.subtitles || []).forEach((sub) => {
      const slide = sub?.slides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title");
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const fileUrl = file?.url;
      entries.push({
        tileId: tile.id,
        slideId: slide.id,
        label: `${sourceLabel}: ${tileLabel} / ${slideLabel}`,
        imageUrl: getSlideImageUrl(sub),
        source: sourceLabel,
        is3D: !!is3D,
        modelUrl: is3D && fileUrl ? fileUrl : null,
      });
    });
  });
  return entries;
}
function getPinLabel(index) {
  const labels = ["A", "B", "C", "D"];
  return labels[index] ?? String.fromCharCode(65 + index);
}
function createSlidePinIcon(label) {
  return L.divIcon({
    className: "slide-pin-marker-wrapper",
    html: `<div style="display:flex;flex-direction:column;align-items:center;line-height:1;pointer-events:auto;">
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:14px solid #28a745;"></div>
      <div style="background:#007bff;color:white;font-weight:bold;font-size:13px;border-radius:4px;padding:3px 8px;margin-top:2px;min-width:22px;text-align:center;box-sizing:border-box;">${label}</div>
    </div>`,
    iconSize: [24, 44],
    iconAnchor: [12, 0],
  });
}
function generateUniqueId() {
  return "id-" + Math.random().toString(36).substr(2, 16);
}
function looksLike3DUrl(url) {
  if (!url || typeof url !== "string") return false;
  const path = url.split("?")[0].toLowerCase();
  return path.endsWith(".glb") || path.endsWith(".gltf");
}

const MAP_CONTAINER_ID = "general-quiz-slide-map";

function ShowQuestions() {
  const { id, lang } = useParams();
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const mapRef = useRef(null);

  const [quizTitleName, setQuizTitleName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [question, onChangeQuestion] = useState("");
  const [level, setLevel] = useState("1");
  const [points1, setPoints1] = useState(5);
  const [answer1, setAnswer1] = useState("");
  const [answer2, setAnswer2] = useState("");
  const [answer3, setAnswer3] = useState("");
  const [answer4, setAnswer4] = useState("");
  const [titleIdForEdit, setTitleIdForEdit] = useState("");
  const [rightAnswer, setRightAnswer] = useState("");
  const [info, setInfo] = useState([]);
  const [tileURL, setTitleURL] = useState("");
  const [questionDialog, setQuestionDialog] = useState(false);
  const [dialogTransitionDone, setDialogTransitionDone] = useState(false);
  const [publicSlideEntries, setPublicSlideEntries] = useState([]);
  const [selectedSlideEntry, setSelectedSlideEntry] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [selectedLang, setSelectedLang] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiAssistExpanded, setAiAssistExpanded] = useState(true);
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuItem, setRowMenuItem] = useState(null);
  const [rowMenuIndex, setRowMenuIndex] = useState(null);
  const [questionsPage, setQuestionsPage] = useState(0);
  const [questionsRowsPerPage, setQuestionsRowsPerPage] = useState(10);

  const pinCountRef = useRef(0);
  const markersRef = useRef([]);

  useEffect(() => {
    fetchQuestions();
  }, [id, lang]);

  useEffect(() => {
    const loadQuizTitleName = async () => {
      try {
        const res = await GetQuizTitlesCall({ lang, type: "histology" });
        if (res?.data && Array.isArray(res.data)) {
          const found = res.data.find((t) => String(t.id) === String(id));
          if (found?.title) setQuizTitleName(found.title);
        }
      } catch (_) {}
    };
    loadQuizTitleName();
  }, [id, lang]);

  useEffect(() => {
    if (!questionDialog) return;
    const langVal = store.getState().general?.lang || lang || "en";
    const load = async () => {
      try {
        const [hisRes, embRes, threeRes] = await Promise.all([
          GetTitlesCall(langVal, "his"),
          GetTitlesCall(langVal, "emb"),
          GetTitlesCall(langVal, "three_d_model"),
        ]);
        const hisTitles = Array.isArray(hisRes) ? hisRes : [];
        const embTitles = Array.isArray(embRes) ? embRes : [];
        const threeTitles = Array.isArray(threeRes) ? threeRes : [];
        setPublicSlideEntries([
          ...buildSlideEntries(hisTitles, "Histology"),
          ...buildSlideEntries(embTitles, "Embryology"),
          ...buildSlideEntries(threeTitles, "3D Model"),
        ]);
      } catch {
        setPublicSlideEntries([]);
      }
    };
    load();
  }, [questionDialog, lang]);

  useEffect(() => {
    if (!questionDialog || publicSlideEntries.length === 0 || !tileURL) return;
    const entry = publicSlideEntries.find((e) =>
      e.is3D ? e.modelUrl === tileURL : getSlideTileUrl(e) === tileURL
    );
    setSelectedSlideEntry(entry || null);
  }, [questionDialog, publicSlideEntries, tileURL]);

  const is3DSelected = selectedSlideEntry?.is3D || looksLike3DUrl(tileURL);
  const slideTileUrl = !is3DSelected && tileURL ? tileURL : "";

  const fetchQuestions = async () => {
    const result = await GetAllQuizByIdCall(id, lang);
    setQuestions(Array.isArray(result) ? result : []);
  };

  const handleGenerateQuestion = async () => {
    setAiError(null);
    setAiLoading(true);
    try {
      const res = await generateQuestionCall({
        prompt: aiPrompt.trim(),
        title: quizTitleName || "",
        slide_context: selectedSlideEntry?.label || "",
        question_type: "text_only",
      });
      if (res && typeof res.status === "number" && res.status >= 400) {
        setAiError(res?.message || "Failed to generate question");
        return;
      }
      const data = res?.data ?? res;
      let answers = Array.isArray(data.answers) ? data.answers.slice(0, 4) : [];
      while (answers.length < 4) answers.push({ answer_text: "", is_correct: false });
      // Shuffle so correct answer isn't always A
      const shuffle = (arr) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };
      answers = shuffle(answers);
      const a1 = answers[0]?.answer_text ?? "";
      const a2 = answers[1]?.answer_text ?? "";
      const a3 = answers[2]?.answer_text ?? "";
      const a4 = answers[3]?.answer_text ?? "";
      const correctIdx = answers.findIndex((a) => a && a.is_correct);
      const rightAnswerIndex = correctIdx >= 0 ? correctIdx + 1 : 1;

      onChangeQuestion(data.question_content || "");
      setAnswer1(a1);
      setAnswer2(a2);
      setAnswer3(a3);
      setAnswer4(a4);
      setRightAnswer(String(rightAnswerIndex));
      const diff = (data.difficulty || "medium").toLowerCase();
      setLevel(diff === "easy" ? "0" : diff === "hard" ? "2" : "1");
      setAiPrompt("");
    } catch (e) {
      setAiError(e?.message || "Something went wrong");
    } finally {
      setAiLoading(false);
    }
  };

  const openDrawer = (quiz, index, lang) => {
    setSelectedQuiz(quiz);
    setSelectedLang(lang);
    setTimeout(() => {
      setDrawerOpen(true);
    }, 400);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedQuiz(null);
    setSelectedLang("");
  };

  const refreshQuizzes = async () => {
    // call your API to refetch all quizzes
  };

  useLayoutEffect(() => {
    if (!questionDialog || !slideTileUrl || !dialogTransitionDone) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = [];
      pinCountRef.current = 0;
      return;
    }
    const el = document.getElementById(MAP_CONTAINER_ID);
    if (!el || mapRef.current) return;
    el.innerHTML = "";
    const map = L.map(MAP_CONTAINER_ID, {
      maxZoom: 10,
      maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      maxBoundsViscosity: 1,
    }).setView([-10.505, -0.09], 2);
    mapRef.current = map;
    L.tileLayer(slideTileUrl, {
      attribution: "&copy; Microlab",
      maxZoom: 10,
      noWrap: true,
      bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
    }).addTo(map);
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({ edit: { featureGroup: drawnItems } });
    map.addControl(drawControl);
    map.on("draw:created", (e) => {
      if (e.layerType !== "marker") return;
      const latlng = e.layer.getLatLng();
      e.layer.remove();
      const labelIndex = pinCountRef.current;
      const label = getPinLabel(labelIndex);
      const marker = L.marker(latlng, { icon: createSlidePinIcon(label) }).addTo(drawnItems);
      const geoJsonItem = {
        type: "Feature",
        geometry: { type: "Point", coordinates: [latlng.lng, latlng.lat] },
        properties: { id: generateUniqueId(), label },
      };
      setInfo((prev) => [...prev, geoJsonItem]);
      markersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
      pinCountRef.current += 1;
    });
    setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 100);
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = [];
      pinCountRef.current = 0;
    };
  }, [questionDialog, slideTileUrl, dialogTransitionDone]);

  useEffect(() => {
    if (!mapRef.current || !slideTileUrl) return;
    const pins = info;
    markersRef.current.forEach((m) => m.marker.remove());
    markersRef.current = [];
    pins.forEach((geoJsonItem) => {
      const [lng, lat] = geoJsonItem.geometry.coordinates;
      const label = geoJsonItem.properties.label;
      const marker = L.marker([lat, lng], { icon: createSlidePinIcon(label) }).addTo(mapRef.current);
      markersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
    });
    pinCountRef.current = pins.length;
    if (pins.length >= 2) {
      const bounds = L.latLngBounds(pins.map((f) => [f.geometry.coordinates[1], f.geometry.coordinates[0]]));
      if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
    }
  }, [info, slideTileUrl]);

  const deletePin = (geoJsonId) => {
    const markerObj = markersRef.current.find((m) => m.geoJsonId === geoJsonId);
    if (!markerObj) return;

    // Remove markers from map
    markerObj.marker.remove();

    markersRef.current = markersRef.current.filter(
      (m) => m.geoJsonId !== geoJsonId
    );

    const deletedLabel = info.find((item) => item.properties.id === geoJsonId)
      ?.properties.label;
    setInfo((prev) => prev.filter((item) => item.properties.id !== geoJsonId));

    if (deletedLabel === "A") setAnswer1("");
    else if (deletedLabel === "B") setAnswer2("");
    else if (deletedLabel === "C") setAnswer3("");
    else if (deletedLabel === "D") setAnswer4("");

    markersRef.current.forEach((m, i) => {
      m.marker.setIcon(createSlidePinIcon(getPinLabel(i)));
    });
    setInfo((prev) =>
      prev.map((item, idx) => ({
        ...item,
        properties: { ...item.properties, label: getPinLabel(idx) },
      }))
    );

    // Update pin counter
    pinCountRef.current = markersRef.current.length;
  };

  const openNewQuestion = () => {
    setTitleIdForEdit("");
    onChangeQuestion("");
    setAnswer1("");
    setAnswer2("");
    setAnswer3("");
    setAnswer4("");
    setRightAnswer("");
    setLevel("1");
    setPoints1(5);
    setTitleURL("");
    setInfo([]);
    setSelectedSlideEntry(null);
    setAiPrompt("");
    setAiError(null);
    setQuestionDialog(true);
  };

  const newQuestion = () => {
    setTitleIdForEdit("");
    onChangeQuestion("");
    setAnswer1("");
    setAnswer2("");
    setAnswer3("");
    setAnswer4("");
    setRightAnswer("");
    setLevel("1");
    setPoints1(5);
    setTitleURL("");
    setInfo([]);
    setSelectedSlideEntry(null);
  };

  const saveQuestion = async () => {
    if (!titleIdForEdit) {
      const body = {
        title_id: id,
        lang,
        title: "no title",
        content: question,
        answer_a: answer1,
        answer_b: answer2,
        answer_c: answer3,
        answer_d: answer4,
        right_answer: rightAnswer,
        point: points1,
        level: level,
        footage: tileURL ?? "",
        footage_type: tileURL ? "slide" : "",
        pins: JSON.stringify(info),
      };
      await AddQuizContentByIdCall(body);
    } else {
      const body = {
        quiz_id: titleIdForEdit,
        lang,
        title: "no title",
        content: question,
        answer_a: answer1,
        answer_b: answer2,
        answer_c: answer3,
        answer_d: answer4,
        right_answer: rightAnswer,
        point: points1,
        level: level,
        footage: tileURL ?? "",
        footage_type: tileURL ? "slide" : "",
        pins: JSON.stringify(info),
      };
      await EditQuizContentByIdCall(body);
    }
    setQuestionDialog(false);
    setDialogTransitionDone(false);
    fetchQuestions();
    newQuestion();
  };

  // Delete question
  const handleDelete = async (item, index) => {
    try {


      const response = await DeleteQuizContentByIdCall({ quiz_id: item.id });
      // Remove question locally
      fetchQuestions();
      alert("Question deleted successfully!");
    } catch (error) {
      console.error(error);
      alert("Error deleting question");
    }
  };

  const handleEdit = (item) => {
    onChangeQuestion(item?.content ?? "");
    setLevel(item?.level ?? "1");
    setPoints1(item?.point ?? 5);
    setAnswer1(item?.answer_a ?? "");
    setAnswer2(item?.answer_b ?? "");
    setAnswer3(item?.answer_c ?? "");
    setAnswer4(item?.answer_d ?? "");
    setRightAnswer(item?.right_answer ?? "");
    setTitleIdForEdit(item?.id ?? "");
    setTitleURL(item?.footage ?? "");
    try {
      const pinsArray = item?.pins ? (typeof item.pins === "string" ? JSON.parse(item.pins) : item.pins) : [];
      setInfo(Array.isArray(pinsArray) ? pinsArray : []);
    } catch {
      setInfo([]);
    }
    setQuestionDialog(true);
  };

  const addLang = (item, index, lang) => {
    if (lang !== "en") {
      openDrawer(item, index, lang);
    } else {
      openDrawer(
        [
          {
            quiz_id: item?.quiz_id,
            title: "",
            answer_a: "",
            answer_b: "",
            answer_c: "",
            answer_d: "",
            content: "",
          },
        ],
        index,
        lang
      );
    }
  };


  const isFormValid =
    answer1 && answer2 && answer3 && answer4 && rightAnswer && question;

  const paginatedQuestions = useMemo(() => {
    const start = questionsPage * questionsRowsPerPage;
    return questions.slice(start, start + questionsRowsPerPage);
  }, [questions, questionsPage, questionsRowsPerPage]);

  const handleQuestionsPageChange = (_, newPage) => setQuestionsPage(newPage);
  const handleQuestionsRowsPerPageChange = (e) => {
    setQuestionsRowsPerPage(parseInt(e.target.value, 10));
    setQuestionsPage(0);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <MDTypography variant="h5" fontWeight="bold" sx={{ color: "text.primary" }}>
            Questions
          </MDTypography>
          <Button variant="contained" color="primary" onClick={openNewQuestion} sx={{ textTransform: "none" }}>
            Add question
          </Button>
        </MDBox>

        {paginatedQuestions.map((item, indexInPage) => {
          const realIndex = questionsPage * questionsRowsPerPage + indexInPage;
          const rawContent = item?.content || "";
          const plainText = stripHtml(rawContent);
          const isLong = plainText.length > QUESTION_TRUNCATE_LENGTH;
          const displayText = isLong ? plainText.slice(0, QUESTION_TRUNCATE_LENGTH) + "..." : plainText;
          return (
            <Card
              key={item.id || realIndex}
              sx={(t) => ({
                mb: 2,
                borderRadius: 2,
                boxShadow: 2,
                overflow: "hidden",
                bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default,
                color: t.palette.text.primary,
                border: "1px solid",
                borderColor: t.palette.divider,
              })}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Tooltip title={plainText || "No question text"} placement="top-start" enterDelay={300}>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 1.5,
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                        borderRadius: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography variant="body2" component="span" sx={{ color: "text.primary" }}>
                        {displayText || "—"}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setRowMenuAnchor(e.currentTarget);
                      setRowMenuItem(item);
                      setRowMenuIndex(realIndex);
                    }}
                    sx={{ color: "text.secondary", flexShrink: 0 }}
                    aria-label="Actions"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </MDBox>
              </CardContent>
            </Card>
          );
        })}

        {questions.length > 0 && (
          <Box sx={(t) => ({ borderTop: 1, borderColor: t.palette.divider, bgcolor: t.palette.background?.card ?? t.palette.background?.paper, borderRadius: 1, mt: 1 })}>
            <TablePagination
              component="div"
              count={questions.length}
              page={questionsPage}
              onPageChange={handleQuestionsPageChange}
              rowsPerPage={questionsRowsPerPage}
              onRowsPerPageChange={handleQuestionsRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={(t) => ({
                color: t.palette.text.primary,
                "& .MuiTablePagination-selectIcon": { color: t.palette.text.primary },
                "& .MuiTablePagination-select": { color: t.palette.text.primary },
              })}
            />
          </Box>
        )}

        <Menu
          anchorEl={rowMenuAnchor}
          open={Boolean(rowMenuAnchor)}
          onClose={() => { setRowMenuAnchor(null); setRowMenuItem(null); setRowMenuIndex(null); }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: (t) => ({ bgcolor: t.palette.background?.card ?? t.palette.background?.paper, color: t.palette.text.primary, minWidth: 160 }) }}
        >
          <MenuItem
            onClick={() => { handleEdit(rowMenuItem); setRowMenuAnchor(null); setRowMenuItem(null); setRowMenuIndex(null); }}
            sx={{ color: "text.primary" }}
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => { addLang(rowMenuItem, rowMenuIndex, "en"); setRowMenuAnchor(null); setRowMenuItem(null); setRowMenuIndex(null); }}
            sx={{ color: "text.primary" }}
          >
            Add language
          </MenuItem>
          {rowMenuItem?.languages?.map((ln) => (
            <MenuItem
              key={ln}
              onClick={() => {
                addLang(rowMenuItem?.translations?.filter((t) => t.lang === ln)?.[0], rowMenuIndex, ln);
                setRowMenuAnchor(null);
                setRowMenuItem(null);
                setRowMenuIndex(null);
              }}
              sx={{ color: "text.primary" }}
            >
              {ln}
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => {
              if (window.confirm("Delete this question?")) handleDelete(rowMenuItem, rowMenuIndex);
              setRowMenuAnchor(null);
              setRowMenuItem(null);
              setRowMenuIndex(null);
            }}
            sx={{ color: "error.main" }}
          >
            Delete
          </MenuItem>
        </Menu>

        <CustomTranslationDrawer
          open={drawerOpen}
          fetchQuestions={fetchQuestions}
          toggleDrawer={closeDrawer}
          lang={selectedLang}
          quiz_id={selectedQuiz?.id}
          data={selectedQuiz}
          refreshQuizzes={refreshQuizzes}
        />

        {/* Add / Edit Question Dialog — same UI as prof QuizSystem */}
        <Dialog
          open={questionDialog}
          onClose={() => { setQuestionDialog(false); setDialogTransitionDone(false); }}
          maxWidth="md"
          fullWidth
          TransitionProps={{ onEntered: () => setDialogTransitionDone(true) }}
          PaperProps={{
            sx: (t) => ({
              // Dark theme uses background.card (no .paper); light uses .paper
              bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default,
              color: t.palette.text.primary,
              border: "1px solid",
              borderColor: t.palette.divider,
            }),
          }}
        >
          <DialogTitle sx={{ color: "text.primary" }}>{titleIdForEdit ? "Edit question" : "Add question"}</DialogTitle>
          <DialogContent sx={(t) => ({ bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default, color: t.palette.text.primary })}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* AI Assist */}
              <Grid item xs={12}>
                <MDBox sx={{ borderRadius: 2, border: "1px solid", borderColor: alpha(theme.palette.primary.main, 0.3), bgcolor: alpha(theme.palette.primary.main, 0.04), overflow: "hidden" }}>
                  <Button fullWidth onClick={() => setAiAssistExpanded((e) => !e)} sx={{ justifyContent: "flex-start", textTransform: "none", py: 1.5, px: 2, color: "text.primary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}>
                    <AutoAwesomeRoundedIcon sx={{ mr: 1, color: "primary.main" }} />
                    <MDTypography variant="button" fontWeight="medium">AI Assist — generate question from title & slide</MDTypography>
                    <Box sx={{ flex: 1 }} />
                    <MDTypography variant="caption" sx={{ color: "text.secondary" }}>{aiAssistExpanded ? "Hide" : "Show"}</MDTypography>
                  </Button>
                  <Collapse in={aiAssistExpanded}>
                    <MDBox sx={{ px: 2, pb: 2, pt: 0 }}>
                      <MDTypography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>Select a slide below (optional) then describe what you want or leave empty.</MDTypography>
                      <TextField fullWidth multiline minRows={2} placeholder="e.g. Ask about the structure at point A" value={aiPrompt} onChange={(e) => { setAiPrompt(e.target.value); setAiError(null); }} size="small" sx={{ mb: 1.5, "& .MuiInputBase-root": { bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
                      {aiError && <MDTypography variant="caption" sx={{ color: "error.main", display: "block", mb: 1 }}>{aiError}</MDTypography>}
                      <Button variant="contained" color="primary" onClick={handleGenerateQuestion} disabled={aiLoading} startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeRoundedIcon />} sx={{ textTransform: "none" }}>{aiLoading ? "Generating…" : "Generate and fill form"}</Button>
                    </MDBox>
                  </Collapse>
                </MDBox>
              </Grid>

              {/* Slide dropdown — all slides + 3D */}
              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="medium" sx={{ color: "text.primary", display: "block", mb: 1 }}>Slide or 3D model (optional)</MDTypography>
                <Autocomplete
                  options={publicSlideEntries}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : "")}
                  value={selectedSlideEntry || null}
                  ListboxProps={{ sx: { maxHeight: 320 } }}
                  onChange={(e, val) => {
                    setSelectedSlideEntry(val);
                    if (!val) { setTitleURL(""); setInfo([]); return; }
                    if (val.is3D && val.modelUrl) { setTitleURL(val.modelUrl); setInfo([]); return; }
                    setTitleURL(getSlideTileUrl(val));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Search slides…" sx={{ ...INPUT_HEIGHT_SX, "& .MuiInputBase-root": { ...INPUT_HEIGHT_SX["& .MuiInputBase-root"], bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} InputProps={{ ...params.InputProps, startAdornment: <><InputAdornment position="start">🔍</InputAdornment>{params.InputProps.startAdornment}</> }} />
                  )}
                  renderOption={(props, opt) => (
                    <li {...props} key={`${opt.tileId}-${opt.slideId}`}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {opt.is3D && opt.modelUrl ? (
                          <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: "hidden", flexShrink: 0, bgcolor: (t) => alpha(t.palette.text.primary, 0.12), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "text.secondary" }}>3D</Box>
                        ) : (
                          <Box component="img" src={opt.imageUrl || noImageUrl} alt="" sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }} />
                        )}
                        <span>{opt.label}</span>
                      </Box>
                    </li>
                  )}
                />
              </Grid>

              {/* Map or 3D preview + pins */}
              {(selectedSlideEntry || tileURL) && (
                <Grid item xs={12}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                    <MDTypography variant="caption" fontWeight="medium" sx={{ color: "text.primary" }}>
                      {is3DSelected ? "Selected 3D model" : "Selected slide — add or delete pins, then save question"}
                    </MDTypography>
                    {!is3DSelected && (
                      <Button size="small" variant="outlined" color="secondary" onClick={() => setInfo([])} disabled={!info.length}>Clear pins</Button>
                    )}
                  </MDBox>
                  {is3DSelected && selectedSlideEntry?.modelUrl ? (
                    <MDBox sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: 380, bgcolor: (t) => alpha(t.palette.text.primary, 0.06) }}>
                      <iframe src={`${typeof window !== "undefined" ? window.location.origin : ""}/model-viewer.html?src=${encodeURIComponent(selectedSlideEntry.modelUrl)}&theme=${darkMode ? "dark" : "light"}`} title="3D" style={{ width: "100%", height: "100%", minHeight: 360, border: "none", display: "block" }} />
                    </MDBox>
                  ) : slideTileUrl ? (
                    <MDBox sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: 380, minHeight: 380 }}>
                      <div id={MAP_CONTAINER_ID} style={{ width: "100%", height: 380, minHeight: 380 }} />
                    </MDBox>
                  ) : null}
                  {!is3DSelected && info.length > 0 && (
                    <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {info.map((item) => (
                        <Chip key={item.properties.id} label={`Pin ${item.properties.label}`} onDelete={() => deletePin(item.properties.id)} size="small" color="primary" variant="outlined" />
                      ))}
                    </MDBox>
                  )}
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={INPUT_HEIGHT_SX} size="small">
                  <InputLabel sx={{ color: "text.secondary" }}>Level</InputLabel>
                  <Select value={level} onChange={(e) => setLevel(e.target.value)} label="Level" MenuProps={MENU_PROPS} sx={{ color: "text.primary", "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }}>
                    <MenuItem value="0">Easy</MenuItem>
                    <MenuItem value="1">Medium</MenuItem>
                    <MenuItem value="2">Hard</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Points" value={points1} onChange={(e) => setPoints1(e.target.value)} size="small" sx={{ ...INPUT_HEIGHT_SX, "& .MuiInputBase-root": { ...INPUT_HEIGHT_SX["& .MuiInputBase-root"], bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} label="Question *" value={question} onChange={(e) => onChangeQuestion(e.target.value)} size="small" sx={{ "& .MuiInputBase-root": { bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
              </Grid>
              {[["Answer 1", answer1, setAnswer1], ["Answer 2", answer2, setAnswer2], ["Answer 3", answer3, setAnswer3], ["Answer 4", answer4, setAnswer4]].map(([lbl, val, setVal], idx) => (
                <Grid item xs={12} key={idx}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Chip size="small" color={rightAnswer === String(idx + 1) ? "success" : "default"} label={`${["A", "B", "C", "D"][idx]} ${rightAnswer === String(idx + 1) ? "(correct)" : ""}`} onClick={() => setRightAnswer(String(idx + 1))} sx={{ color: "text.primary" }} />
                    <TextField fullWidth size="small" label={lbl} value={val} onChange={(e) => setVal(e.target.value)} sx={{ "& .MuiInputBase-root": { bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={(t) => ({ color: t.palette.text.primary, bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default, borderTop: 1, borderColor: t.palette.divider })}>
            <Button onClick={() => { setQuestionDialog(false); setDialogTransitionDone(false); }} color="inherit">Cancel</Button>
            <Button variant="contained" onClick={saveQuestion} disabled={!isFormValid} sx={{ color: "white" }}>Save</Button>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default ShowQuestions;
